'use client';

import { useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectItem,
  Textarea,
  Autocomplete,
  AutocompleteItem,
} from '@heroui/react';
import useClientStore from '@/store/useClientStore';
import useCustomerStore from '@/store/useCustomerStore';
import { supabase } from '@/lib/supabase';
import { LeadTypes, Vehicle } from '@/utils/types';
import { sendEmail, createFinancingLeadEmailTemplate } from '@/lib/send-email2';
import { sendSiteFormEmail, idNumerico } from '@/lib/site-form-email';
import SuccessModal from '@/components/ui/SuccessModal';
import { useEnsureVehicles } from '@/hooks/useEnsureVehicles';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { useCurrency } from '@/hooks/useCurrency';
import { isBrasil, customerDocOf, forcedSiteLanguage } from '@/lib/country';
import { opcionesDeEmpleo } from '@/lib/textos-usa';

interface FormStyleProps {
  title?: string;
  subtitle?: string;
  bgColor?: string;
  textColor?: string;
  accentColor?: string;
  embedded?: boolean;
}

const FinancingForm = ({ title, subtitle, bgColor, textColor, accentColor, embedded = false }: FormStyleProps = {}) => {
  const { formatPrice, locale, symbol } = useCurrency();
  // When builder passes bgColor, use inline styles. Otherwise use default Tailwind classes.
  const hasBuilderStyles = !!bgColor;
  const isDarkBg = bgColor && (bgColor.startsWith('#0') || bgColor.startsWith('#1') || bgColor.startsWith('#2'));
  const cardStyle = hasBuilderStyles
    ? { backgroundColor: bgColor, borderColor: textColor ? `${textColor}15` : undefined }
    : undefined;
  const cardClass = hasBuilderStyles
    ? 'rounded-xl shadow-lg p-8 border'
    : 'bg-white dark:bg-dark-card rounded-xl shadow-lg p-8 border border-gray-200 dark:border-dark-border';
  const infoCardStyle = hasBuilderStyles
    ? { backgroundColor: bgColor ? `${bgColor}f0` : undefined, borderColor: textColor ? `${textColor}15` : undefined }
    : undefined;
  const infoCardClass = hasBuilderStyles
    ? 'rounded-xl p-8 border'
    : 'bg-gray-50 dark:bg-dark-card rounded-xl p-8 border border-gray-200 dark:border-dark-border';
  const titleStyle = hasBuilderStyles ? { color: textColor } : undefined;
  const titleClass = hasBuilderStyles ? 'text-3xl sm:text-4xl font-extrabold' : 'text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white';
  const subtitleStyle = hasBuilderStyles ? { color: textColor, opacity: 0.6 } : undefined;
  const subtitleClass = hasBuilderStyles ? 'mt-4 text-lg' : 'mt-4 text-lg text-gray-500 dark:text-gray-400';
  const headingStyle = hasBuilderStyles ? { color: textColor } : undefined;
  const headingClass = hasBuilderStyles ? 'text-2xl font-bold' : 'text-2xl font-bold text-gray-900 dark:text-white';
  const subHeadingClass = hasBuilderStyles ? 'text-lg font-medium' : 'text-lg font-medium text-gray-900 dark:text-white';
  const bodyStyle = hasBuilderStyles ? { color: textColor, opacity: 0.7 } : undefined;
  const bodyClass = hasBuilderStyles ? '' : 'text-gray-600 dark:text-gray-400';

  // HeroUI input classNames to match builder theme
  const inputClassNames = (hasBuilderStyles || embedded) ? {
    label: isDarkBg ? '!text-white/60' : '!text-black/50',
    input: isDarkBg ? '!text-white !placeholder-white/40' : '!text-gray-900',
    inputWrapper: isDarkBg
      ? '!bg-[#262626] !border-[#3a3a3a] hover:!border-[#4a4a4a] !rounded-lg'
      : '!bg-white !border-[#d1d5db] hover:!border-gray-400 !rounded-lg',
  } : undefined;
  const buttonStyle = (hasBuilderStyles || embedded) && accentColor ? { backgroundColor: accentColor } : undefined;

  const { vehicles } = useEnsureVehicles();
  const { client } = useClientStore();
  const { initializeCustomer } = useCustomerStore();
  const { t } = useTranslation();
  // Los correos los lee la automotora: en Brasil, en portugués. El campo "RUT"
  // ya sale como "CPF" por la traducción pt (financing.form.rut) y no se valida
  // con formato chileno en ningún país.
  const esBrasil = isBrasil(client?.country);
  // EE.UU.: lo que lee la automotora (correo y asuntos), en inglés.
  const enUsa = forcedSiteLanguage(client?.country) === 'en';
  // Documento del cliente por PAÍS (lib/country): CO "NIT/Cédula", AR
  // "CUIT/DNI", con ejemplo. null (CL, BR, sin país) = el campo de siempre.
  // En ningún país se valida: un lead no se pierde por cómo se escribió el
  // documento.
  const docPais = customerDocOf(client?.country);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    vehicle_id: '',
    message: '',
    down_payment: '',
    monthly_income: '',
    employment_type: '',
    rut: '',
    birth_date: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      // 1. Create/initialize customer
      const customerResponse = await initializeCustomer({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        client_id: client?.id || '',
        rut: formData.rut,
        birth_date: formData.birth_date,
      });

      // 2. Create lead with vehicle_id if selected
      const { error: leadError } = await supabase.from('leads').insert([
        {
          client_id: client?.id,
          customer_id: customerResponse.id,
          vehicle_id: formData.vehicle_id || null,
          type: LeadTypes.SELL_FINANCING,
          status: 'pending',
          notes: formData.message,
          financing_data: {
            monthly_income: getNumericValue(formData.monthly_income),
            down_payment: getNumericValue(formData.down_payment),
            employment_type: formData.employment_type,
          },
        },
      ]);

      if (leadError) throw leadError;

      // 3. Send email notification
      // Valor "sin dato" del correo (lo lee la automotora, en su idioma).
      const noEsp = esBrasil ? 'Não informado' : enUsa ? 'Not provided' : 'No especificado';
      const noEspF = esBrasil ? 'Não informada' : enUsa ? 'Not provided' : 'No especificada';

      const selectedVehicle: Vehicle | undefined = vehicles.find(
        (v) => v.id.toString() === formData.vehicle_id.toString()
      );

      console.log('Selected vehicle found:', selectedVehicle);

      let vehicleInfo = {
        brand: noEsp,
        model: noEsp,
        year: '',
        mileage: '',
        condition: 'N/A',
        price: '',
        plate: '',
      };

      if (selectedVehicle) {
        vehicleInfo = {
          brand: selectedVehicle.brand?.name || noEsp,
          model: selectedVehicle.model?.name || noEsp,
          // La patente NO viaja al navegador: el select de vehículos ya no puede
          // pedir license_plate (revocada a anon el 26-jul por el pentest), así
          // que va un placeholder y la edge function send-email lo reemplaza con
          // el dato de la base. Sin esto el correo mostraba "No especificado".
          plate: '{{VEHICLE_PLATE}}',
          year: selectedVehicle.year?.toString() || '',
          mileage: selectedVehicle.mileage?.toString() || '',
          condition: selectedVehicle.condition?.name || 'N/A',
          price: selectedVehicle.price
            ? Number(selectedVehicle.price).toLocaleString(locale)
            : '',
        };
      }

      console.log('Vehicle info being sent:', vehicleInfo);

      // Use the email function
      const datosCorreo = {
        customerName: `${formData.first_name} ${formData.last_name}`,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        customerRut: formData.rut,
        vehicleDetails: vehicleInfo,
        // Formato INTERNO (rótulos siempre en español, también en Brasil): la
        // plantilla lo parsea por estos rótulos y no lo muestra tal cual.
        additionalMessage: `
Información Financiera:
- Actividad Laboral: ${formData.employment_type || noEsp}
- Renta Líquida Mensual: ${symbol}${formData.monthly_income || '0'}
- Monto Pie: ${symbol}${formData.down_payment || '0'}
- Fecha de Nacimiento: ${formData.birth_date || noEspF}

${formData.message ? `Mensaje del cliente:\n${formData.message}` : ''}`,
        currencySymbol: symbol,
        country: client?.country,
      };
      const emailContent = createFinancingLeadEmailTemplate(datosCorreo);

      // Determinar emails de destino para financiamiento
      const financeEmails =
        client?.contact?.finance_emails &&
        client.contact.finance_emails.length > 0
          ? client.contact.finance_emails
          : [client?.contact?.email || ''];

      // Enviar email al cliente/automotora
      // Destinatarios/asunto/HTML (y la patente) los arma la edge
      // (`site_form`); si no puede, cae a este mismo envío de siempre. Asunto:
      // con vehículo (`withVehicle`) lleva params.vehicleDetails.brand/model.
      const emailResult = await sendSiteFormEmail(
        {
          client_id: idNumerico(client?.id) ?? 0,
          kind: 'lead_finance',
          vehicle_id: selectedVehicle ? idNumerico(selectedVehicle.id) : null,
          params: { ...datosCorreo, withVehicle: !!selectedVehicle },
        },
        {
          to: financeEmails,
          subject: esBrasil
            ? `Solicitação de financiamento${
                selectedVehicle ? `: ${vehicleInfo.brand} ${vehicleInfo.model}` : ''
              }`
            : enUsa
            ? `Financing request${
                selectedVehicle ? `: ${vehicleInfo.brand} ${vehicleInfo.model}` : ''
              }`
            : `Solicitud de Financiamiento${
            selectedVehicle ? `: ${vehicleInfo.brand} ${vehicleInfo.model}` : ''
          }`,
          content: emailContent,
          resolve_vehicle_plate: selectedVehicle ? Number(selectedVehicle.id) : undefined,
        },
        sendEmail
      );

      if (!emailResult.success) {
        console.error(
          'Error al enviar el email a la automotora:',
          emailResult.error
        );
        // Continue with flow even if email fails
      }

      // Enviar email al vendedor si el vehículo tiene un seller_id asociado
      if (selectedVehicle?.seller_id) {
        try {
          // El vendedor (y la patente) los resuelve la edge desde vehicle_id
          // (`site_form`): anon ya no puede leer `users` (pentest 30-oct).
          // Asunto: params.vehicleDetails.brand/model.
          const sellerEmailResult = await sendSiteFormEmail(
            {
              client_id: idNumerico(client?.id) ?? 0,
              kind: 'lead_finance_seller',
              vehicle_id: idNumerico(selectedVehicle.id),
              params: { ...datosCorreo, withVehicle: true },
            },
            null
          );

          if (!sellerEmailResult.success) {
            console.error(
              'Error al enviar el email al vendedor:',
              sellerEmailResult.error
            );
          }
        } catch (sellerEmailError) {
          console.error(
            'Error al procesar el envío de email al vendedor:',
            sellerEmailError
          );
        }
      }

      /*    // Clear form after successful submission
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        vehicle_id: '',
        message: '',
        down_payment: '',
        monthly_income: '',
        employment_type: '',
        rut: '',
        birth_date: '',
      });
 */
      // Show success modal
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error al procesar el formulario:', error);

      // More specific message based on error type
      let errorMessage = t('formsUi.errors.generic');

      if (error.message?.includes('email')) {
        errorMessage = t('formsUi.errors.emailNotSent');
      } else if (error.code === '23505') {
        errorMessage = t('formsUi.errors.duplicate');
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value: string, id: string) => {
    if (id === 'monthly_income' || id === 'down_payment') {
      // Remove non-numeric characters
      const numericValue = value.replace(/\D/g, '');

      // Format with thousand separators
      const formattedValue =
        numericValue === '' ? '' : Number(numericValue).toLocaleString(locale);

      setFormData((prev) => ({
        ...prev,
        [id]: formattedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  // Helper to get clean numeric value from formatted string
  const getNumericValue = (formattedValue: string): number => {
    return parseInt(formattedValue.replace(/\D/g, '') || '0');
  };

  // Helper to format vehicle information for display
  const formatVehicleOption = (vehicle: any) => {
    const brand = vehicle.brand?.name || '';
    const model = vehicle.model?.name || '';
    const year = vehicle.year || '';
    const price = vehicle.price ? formatPrice(Number(vehicle.price)) : '';

    return `${brand} ${model} (${year}) - ${price}`;
  };

  // Opciones de actividad laboral. El VALOR (lo que se guarda en el lead y va
  // en el correo) es el texto del diccionario, como siempre; EE.UU. solo ve
  // otra etiqueta (su clasificación laboral, no la chilena).
  const employmentTypes = opcionesDeEmpleo(t, enUsa);

  return (
    <div data-form-section="financing">
      {!embedded && title && (
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <h1 className={titleClass} style={titleStyle}>{title}</h1>
          {subtitle && <p className={subtitleClass} style={subtitleStyle}>{subtitle}</p>}
        </div>
      )}
      <div className={embedded ? '' : (hasBuilderStyles ? 'max-w-2xl mx-auto' : 'grid grid-cols-1 md:grid-cols-2 gap-16')}>
        {/* Financing Form */}
        <div className={embedded ? '' : cardClass} style={embedded ? undefined : cardStyle}>
          <form onSubmit={handleSubmit} className='space-y-6'>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <Input
                type='text'
                label={t('financing.form.firstName')}
                autoComplete='given-name'
                value={formData.first_name}
                onValueChange={(value) => handleChange(value, 'first_name')}
                isRequired
                variant='bordered'
                classNames={inputClassNames}
              />
              <Input
                type='text'
                label={t('financing.form.lastName')}
                autoComplete='family-name'
                value={formData.last_name}
                onValueChange={(value) => handleChange(value, 'last_name')}
                isRequired
                variant='bordered'
                classNames={inputClassNames}
              />
            </div>

            <Input
              type='email'
              label={t('financing.form.email')}
              autoComplete='email'
              value={formData.email}
              onValueChange={(value) => handleChange(value, 'email')}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            />

            <Input
              type='tel'
              label={t('financing.form.phone')}
              autoComplete='tel'
              value={formData.phone}
              onValueChange={(value) => handleChange(value, 'phone')}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            />

            <Input
              type='text'
              label={docPais?.label ?? t('financing.form.rut')}
              value={formData.rut}
              onValueChange={(value) => handleChange(value, 'rut')}
              isRequired={!docPais?.optional}
              variant='bordered'
              classNames={inputClassNames}
              {...(docPais ? { placeholder: docPais.placeholder } : {})}
            />

            <Input
              type='date'
              label={t('financing.form.birthDate')}
              value={formData.birth_date}
              onValueChange={(value) => handleChange(value, 'birth_date')}
              isRequired
              variant='bordered'
              placeholder=' '
              classNames={inputClassNames}
            />

            {/* Actividad Laboral */}
            <Select
              label={t('financing.form.employmentType')}
              selectedKeys={
                formData.employment_type ? [formData.employment_type] : []
              }
              onChange={(e) => handleChange(e.target.value, 'employment_type')}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            >
              {employmentTypes.map((opcion) => (
                <SelectItem key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </SelectItem>
              ))}
            </Select>

            {/* Renta Líquida Mensual */}
            <Input
              type='text'
              label={t('financing.form.monthlyIncome')}
              inputMode='numeric'
              value={formData.monthly_income}
              onValueChange={(value) => handleChange(value, 'monthly_income')}
              startContent={symbol}
              isRequired
              variant='bordered'
              classNames={inputClassNames}
            />

            {/* Vehicle Selection */}
            {vehicles.filter((v) => v?.status?.name === 'Publicado').length >
            0 ? (
              <Autocomplete
                label={t('financing.form.vehicleInterest')}
                placeholder={t('financing.form.vehicleSearchPlaceholder')}
                selectedKey={formData.vehicle_id}
                onSelectionChange={(key) =>
                  handleChange(key as string, 'vehicle_id')
                }
                isRequired
                variant='bordered'
                inputProps={{ classNames: inputClassNames }}
              >
                {vehicles
                  .filter((v) => v?.status?.name === 'Publicado')
                  .map((vehicle) => (
                    <AutocompleteItem key={vehicle.id} value={vehicle.id}>
                      {formatVehicleOption(vehicle)}
                    </AutocompleteItem>
                  ))}
              </Autocomplete>
            ) : (
              <div className='p-4 text-center text-gray-500 border border-gray-200 rounded-md'>
                {t('financing.form.noVehiclesAvailable')}
              </div>
            )}

            {/* Monto Pie */}
            <Input
              type='text'
              label={t('financing.form.downPayment')}
              inputMode='numeric'
              value={formData.down_payment}
              onValueChange={(value) => handleChange(value, 'down_payment')}
              isRequired
              variant='bordered'
              startContent={symbol}
              classNames={inputClassNames}
            />

            <Textarea
              label={t('financing.form.message')}
              value={formData.message}
              onValueChange={(value) => handleChange(value, 'message')}
              minRows={4}
              variant='bordered'
              classNames={inputClassNames}
            />

            <Button
              type='submit'
              color='primary'
              fullWidth
              className={'h-12 sm:h-10 ' + (embedded ? 'font-semibold !text-white hover:opacity-90' : 'font-semibold bg-primary text-white hover:bg-primary/90 dark:bg-primary dark:text-white dark:hover:bg-primary/90')}
              style={buttonStyle}
              isLoading={loading}
            >
              {t('financing.form.submit')}
            </Button>
          </form>
        </div>

        {/* Information Section */}
        {!embedded && !hasBuilderStyles && (
        <div className={infoCardClass} style={infoCardStyle}>
          <div className='space-y-8'>
            <h2 className={headingClass} style={headingStyle}>
              {t('financing.info.optionsTitle')}
            </h2>

            <div>
              <h3 className={subHeadingClass} style={headingStyle}>
                {t('financing.info.benefitsTitle')}
              </h3>
              <ul className={`mt-2 space-y-2 list-disc pl-5 ${bodyClass}`} style={bodyStyle}>
                <li>{t('financing.info.benefitsList.item1')}</li>
                <li>{t('financing.info.benefitsList.item2')}</li>
                <li>{t('financing.info.benefitsList.item3')}</li>
                <li>{t('financing.info.benefitsList.item4')}</li>
              </ul>
            </div>

            <div>
              <h3 className={subHeadingClass} style={headingStyle}>
                {t('financing.info.requirementsTitle')}
              </h3>
              <ul className={`mt-2 space-y-2 list-disc pl-5 ${bodyClass}`} style={bodyStyle}>
                <li>{t('financing.info.requirementsList.item1')}</li>
                <li>{t('financing.info.requirementsList.item2')}</li>
                <li>{t('financing.info.requirementsList.item3')}</li>
                <li>{t('financing.info.requirementsList.item4')}</li>
              </ul>
            </div>

            <div>
              <h3 className={subHeadingClass} style={headingStyle}>
                {t('financing.info.directContactTitle')}
              </h3>
              <p className={`mt-2 ${bodyClass}`} style={bodyStyle}>
                {t('financing.info.emailLabel')}: {client?.contact?.email}
                <br />
                {t('financing.info.phoneLabel')}: {client?.contact?.phone}
              </p>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        leadType={LeadTypes.SELL_FINANCING}
      />
    </div>
  );
};

export default FinancingForm;
